import { useCallback, useRef } from "react";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import type { NavigationState } from "@react-navigation/routers";
import { EarningType } from "../__generated__/graphql";
import { useSetAtom } from "jotai";
import { editVacationCallback } from "../atoms/core";

type ScreenParams = StackScreenProps<RootStackParamList>;

export type VacationEditorResult = {
    vacationDays?: number;
    payVacationIndemnityWithSalary: boolean;
};

type CallBackType = (result: VacationEditorResult) => void | Promise<void>;

type CallBackRefType = {
    callback: CallBackType;
    state: NavigationState<RootStackParamList>;
};

const extractLastRouteName = (
    state: NavigationState<RootStackParamList>,
): Extract<keyof RootStackParamList, string> | undefined => {
    const routes = state.routes;
    if (routes.length === 0) {
        return undefined;
    }
    const lastRoute = routes[routes.length - 1];
    return lastRoute.name;
};

/**
 * Brings the vacation editor on screen (number of days + "cagnotte vacances" choice).
 * usage:
 * const editVacation = useVacationEditor();
 * editVacation({ vacationDays, payVacationIndemnityWithSalary, isCagnotteMandatory, ... }, (result) => {
 *       // result.vacationDays / result.payVacationIndemnityWithSalary have been chosen
 *   });
 */
export const useVacationEditor = () => {
    const callbackRef = useRef<CallBackRefType>(undefined);
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const setOnSave = useSetAtom(editVacationCallback);
    return useCallback(
        (
            options: {
                vacationDays?: number;
                payVacationIndemnityWithSalary: boolean;
                isCagnotteMandatory: boolean;
                isVacationTracked: boolean;
                minVacationDays: number;
                contractPercentage: number;
                earningType: EarningType;
            },
            onChange: CallBackType,
        ) => {
            const state = navigation.getState();
            callbackRef.current = { callback: onChange, state };

            const onSave = async (result: VacationEditorResult) => {
                const callback = callbackRef.current?.callback;
                const savedState = callbackRef.current?.state;

                await callback?.(result);

                callbackRef.current = undefined;

                if (savedState) {
                    const lastRouteName = extractLastRouteName(savedState);
                    if (lastRouteName) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        navigation.navigate(lastRouteName);
                        return;
                    }
                }
                navigation.pop();
            };
            setOnSave({ func: onSave });
            navigation.navigate("VacationEditorNavScreen", {
                vacationDays: options.vacationDays,
                payVacationIndemnityWithSalary: options.payVacationIndemnityWithSalary,
                isCagnotteMandatory: options.isCagnotteMandatory,
                isVacationTracked: options.isVacationTracked,
                minVacationDays: options.minVacationDays,
                contractPercentage: options.contractPercentage,
                earningType: options.earningType,
            });
        },
        [navigation, setOnSave],
    );
};
